import BaseApi from '@core/api/model';

import PreferenceTypeEnum from '@constants/enums/preference/type';

import Email from '@/models/Email';
import NotificationPreference from '@/models/NotificationPreference';

export class UserApi extends BaseApi {
    static resource = 'users';

    get endpoints() {
        return {
            ...super.endpoints,
            verifyEmail: this.verifyEmail.bind(this),
            setupMfa: this.setupMfa.bind(this),
            logoutAllDevices: this.logoutAllDevices.bind(this),
            getTerms: this.getTerms.bind(this),
            acceptTerms: this.acceptTerms.bind(this),
            downloadTerms: this.downloadTerms.bind(this),
            getPreferences: this.getPreferences.bind(this),
            updatePreferences: this.updatePreferences.bind(this),
            getEmails: this.getEmails.bind(this),
            getVisibility: this.getVisibility.bind(this),
            listRoles: this.listRoles.bind(this),
            createRole: this.createRole.bind(this),
            updateRole: this.updateRole.bind(this),
            deleteRole: this.deleteRole.bind(this)
        };
    }

    get mock_endpoints() {
        return {
            ...super.mock_endpoints,
            getEmails: this.getEmails_mock.bind(this)
        };
    }

    async verifyEmail(id, email) {
        const response = await BaseApi.api.post(`${this.base_url}/${id}/verify-email`, { email });

        return this._respond(response, () => {
            this._updateItemInCollections(id, response.data);
        });
    }

    async setupMfa(id) {
        const response = await BaseApi.api.post(`${this.base_url}/${id}/setup-mfa`);

        return this._respond(response, () => {
            this._updateItemInCollections(id, response.data);
        });
    }

    async logoutAllDevices(id) {
        return await BaseApi.api.get(`${this.base_url}/${id}/forcelogout`);
    }

    async getTerms(userId) {
        const response = await BaseApi.api.get(`${this.base_url}/${userId}/terms`);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
            response.data = response.data.map(t => ({ status: 'APPROVED', ...t }));
        });
    }

    async acceptTerms(userId, terms) {
        const response = await BaseApi.api.post(`${this.base_url}/${userId}/terms`, terms);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async downloadTerms(userId) {
        let url = `${this.base_url}`;

        if (userId) {
            url += `/${userId}`;
        }

        url += '/terms/download';

        return await BaseApi.api.get(
            url,
            {},
            {
                headers: {
                    Accept: 'application/pdf'
                },
                responseType: 'blob',
                returnFullResponse: true
            }
        );
    }

    async getPreferences(userId, type = null, params = {}) {
        if (!userId) {
            throw new Error('Missing ID required while attempting to fetch preferences');
        }

        if (type && !PreferenceTypeEnum[`${type}`]) {
            throw new Error(`Invalid preference type: ${type}`);
        }

        let urlTypeSlug = null;

        switch (type) {
            case PreferenceTypeEnum.NOTIFICATIONS:
                urlTypeSlug = 'notifications';
                break;
        }

        let url = `${this.base_url}/${userId}/preferences`;

        if (type) {
            url += `/${urlTypeSlug}`;
        }

        const response = await BaseApi.api.get(url, params);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);

            if (type === PreferenceTypeEnum.NOTIFICATIONS) {
                // response.data.push({
                //     id: 'fdeefd6c-9f8c-48cf-8d2e-a55d41c28a21',
                //     organisation_id: '5df4a425-0098-4c52-b9ab-a4977f664927',
                //     type: 'DAILY_NOTIFICATION_REMINDER',
                //     email: true
                // });
                response.data = response.data.map(preference => new NotificationPreference(preference));
            }
        });
    }

    async updatePreferences(userId, type, preferences = [], params = {}) {
        if (!userId) {
            throw new Error('Missing ID required while attempting to update preferences');
        }

        if (type && !PreferenceTypeEnum[`${type}`]) {
            throw new Error(`Invalid preference type: ${type}`);
        }

        let urlTypeSlug = null;

        switch (type) {
            case PreferenceTypeEnum.NOTIFICATIONS:
                urlTypeSlug = 'notifications';
                break;
        }

        let url = `${this.base_url}/${userId}/preferences`;

        if (type) {
            url += `/${urlTypeSlug}`;
        }

        const response = await BaseApi.api.patch(url, preferences, { params });

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);

            if (type === PreferenceTypeEnum.NOTIFICATIONS) {
                response.data = response.data.map(preference => new NotificationPreference(preference));
            }
        });
    }

    async getEmails(userId, scopeId = null) {
        const params = {};
        if (scopeId) {
            params.organisation_id = scopeId;
        }

        const response = await BaseApi.api.get(`${this.base_url}/${userId}/emails`, params);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async getEmails_mock() {
        const limit = 12;
        const offset = 0;

        let data = localStorage.getItem(`${this.mockStorageKey}-emails`);

        if (!data) {
            localStorage.setItem(`${this.mockStorageKey}-emails`, JSON.stringify([]));
        }

        data = data ? JSON.parse(data) : [];

        if (!data.length) {
            data = await BaseApi.mock(Email.mock(), limit);
            localStorage.setItem(`${this.mockStorageKey}-emails`, JSON.stringify([]));
        }

        let response = {
            status: 'success',
            data,
            metadata: {
                total: data.length,
                limit,
                offset
            }
        };

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    async getVisibility(userId, filters = {}, config = {}) {
        const response = await BaseApi.api.get(`${this.base_url}/${userId}/visibility`, filters, config);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    /**
     * List all role assignments for a user (full model per row).
     * @param {string} userId
     */
    async listRoles(userId) {
        if (!userId) {
            throw new Error('Missing user ID required while attempting to list roles');
        }

        const response = await BaseApi.api.get(`${this.base_url}/${userId}/roles`);

        return this._respond(response, () => {
            response.data = this._filterResponseData(response.data);
        });
    }

    /**
     * Create a role assignment for a user on an organisation.
     * @param {string} userId
     * @param {string} organisationId
     * @param {Object} body e.g. { roles: ['ADVISER'] } (at most one role in the array)
     */
    async createRole(userId, organisationId, body = {}) {
        if (!userId || !organisationId) {
            throw new Error('Missing user ID or organisation ID while attempting to create a role');
        }

        const response = await BaseApi.api.post(`${this.base_url}/${userId}/roles/${organisationId}`, body);

        return this._respond(response, () => {
            if (response.data) {
                response.data = this._filterResponseData(response.data);
            }
        });
    }

    /**
     * Update a role assignment for a user on an organisation.
     * @param {string} userId
     * @param {string} organisationId
     * @param {Object} body e.g. { roles: ['ADVISER'] }
     */
    async updateRole(userId, organisationId, body = {}) {
        if (!userId || !organisationId) {
            throw new Error('Missing user ID or organisation ID while attempting to update a role');
        }

        const response = await BaseApi.api.patch(`${this.base_url}/${userId}/roles/${organisationId}`, body);

        return this._respond(response, () => {
            if (response.data) {
                response.data = this._filterResponseData(response.data);
            }
        });
    }

    /**
     * Remove a role assignment for a user on an organisation.
     * @param {string} userId
     * @param {string} organisationId
     */
    async deleteRole(userId, organisationId) {
        if (!userId || !organisationId) {
            throw new Error('Missing user ID or organisation ID while attempting to delete a role');
        }

        const response = await BaseApi.api.remove(`${this.base_url}/${userId}/roles/${organisationId}`);

        return this._respond(response, () => {
            if (response.data) {
                response.data = this._filterResponseData(response.data);
            }
        });
    }
}

export default UserApi;
